import styled, { keyframes } from 'styled-components/macro';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconTel,
  IconReturnCopy,
  IconCard,
  IconFrame124,
  IconAlmaLogo,
  IconInfo,
  IconCbLogo,
  IconVisaLogo,
  IconAmericanExpressLogo,
  IconAmericanExpressLogo1,
  IconPaypalLogo,
  IconLogoPaiement,
} from 'Ui/Icons';
import AlmaModal from 'Components/modals/AlmaModal';

const fadeSlide = keyframes`
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SliderRow = styled.div`
  animation: ${fadeSlide} 0.3s ease;
  display: flex;
  justify-content: center;
  min-height: 5rem;
  margin: auto;
`;

/* ── Item wrapper ── */

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 0.4rem 0;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  text-align: center;
  width: 100%;
  justify-content: center;
`;

const IconWrap = styled.span`
  font-size: 2rem;
  flex-shrink: 0;
  color: #000;
  line-height: 1;
  svg path,
  svg circle,
  svg rect,
  svg line,
  svg polyline,
  svg polygon {
    stroke: #000;
    stroke-width: 1;
  }
`;

const Title = styled.span`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #000;
`;

const Body = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 400;
  width: 100%;
  justify-content: center !important;
  color: #000;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  text-align: center;

  strong {
    font-weight: 700;
    color: #000;
  }
`;

const AlmaLogoWrapper = styled.span`
  display: inline-flex;
  align-items: center;
`;

const AlmaLogoInline = styled(IconAlmaLogo)`
  width: 3.6rem;
  height: 1.1rem;
  flex-shrink: 0;
`;

const InfoCircle = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  /* color: #969696; */
  cursor: pointer;
  flex-shrink: 0;
  border: none;
  background-color: transparent;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const LogosSlide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 5rem;
`;

const CardsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

/* ── Individual items ── */
export const SvgMail = (props: any) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="1em"
    height="1em"
    fill="none"
    viewBox="0 0 31 24"
    {...props}
  >
    <path
      fill="url(#mail_svg__a)"
      stroke="url(#mail_svg__b)"
      strokeMiterlimit={10}
      d="M26.38 1H4.62C2.628 1 1 2.687 1 4.75v14.5C1 21.322 2.628 23 4.62 23h21.76c2.002 0 3.62-1.687 3.62-3.75V4.75C30 2.678 28.372 1 26.38 1ZM4.62 2.064h21.76c.923 0 1.732.494 2.189 1.247l-11.16 10.92a2.74 2.74 0 0 1-3.817 0L2.432 3.31A2.56 2.56 0 0 1 4.62 2.064Zm-2.572 17.54c-.01-.118-.021-.237-.021-.355V4.751c0-.118 0-.237.02-.355l7.77 7.599-7.77 7.598zm24.332 2.332H4.62a2.56 2.56 0 0 1-2.189-1.247l8.132-7.953 2.323 2.279a3.7 3.7 0 0 0 2.614 1.053c.944 0 1.888-.355 2.614-1.053l2.323-2.279 8.132 7.953a2.56 2.56 0 0 1-2.189 1.247Zm2.593-17.185v14.498c0 .118-.01.237-.02.355l-7.77-7.599 7.77-7.598c.01.118.02.236.02.355z"
    />
    <defs>
      <linearGradient
        id="mail_svg__a"
        x1={15.5}
        x2={15.5}
        y1={1}
        y2={23}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#6C4DFF" />
        <stop offset={1} stopColor="#8268FF" />
      </linearGradient>
      <linearGradient
        id="mail_svg__b"
        x1={15.5}
        x2={15.5}
        y1={1}
        y2={23}
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#6C4DFF" />
        <stop offset={1} stopColor="#8268FF" />
      </linearGradient>
    </defs>
  </svg>
);
function ServiceClient() {
  const { t } = useTranslation('shipping', { useSuspense: false });
  return (
    <Wrapper>
      <Header>
        <IconWrap>
          <SvgMail />
        </IconWrap>
        <Title>{t('serviceClientGridTitle')}</Title>
      </Header>
      <Body>
        {t('serviceClientGridHours')}{' '}
        <a style={{ textDecoration: 'underline' }} href="/contact/question/">
          {t('serviceClientGridLink')}
        </a>
      </Body>
    </Wrapper>
  );
}

function EasyReturns() {
  const { t } = useTranslation('shipping', { useSuspense: false });
  return (
    <Wrapper>
      <Header>
        <IconWrap>
          <IconReturnCopy />
        </IconWrap>
        <Title>{t('easyReturnsGridTitle')}</Title>
      </Header>
      <Body>{t('easyReturnsDays')}</Body>
    </Wrapper>
  );
}

function AlmaInstallments() {
  const { t } = useTranslation('shipping', { useSuspense: false });
  return (
    <Wrapper>
      <Header>
        <IconWrap>
          <IconCard />
        </IconWrap>
        <Title>{t('almaGridTitle')}</Title>
      </Header>
      <Body>
        {t('almaGridSub')}
        <AlmaLogoWrapper>
          <AlmaLogoInline />
        </AlmaLogoWrapper>
        <InfoCircle
          type="button"
          aria-label={t('almaMoreInfo')}
          onClick={() => AlmaModal({ modalName: 'split_payment_infos_pdp' })}
        >
          <IconInfo />
        </InfoCircle>
      </Body>
    </Wrapper>
  );
}

function SecurePayment() {
  const { t } = useTranslation('shipping', { useSuspense: false });
  return (
    <Wrapper>
      <Header>
        <IconWrap>
          <IconFrame124 />
        </IconWrap>
        <Title>{t('securePaymentTitle')}</Title>
      </Header>
      <Body>{t('securePaymentSub')}</Body>
    </Wrapper>
  );
}

function PaymentLogos() {
  return (
    <LogosSlide>
      <CardsRow>
        <IconCbLogo width={35} height={22} />
        <IconVisaLogo width={35} height={22} />
        <IconAmericanExpressLogo width={35} height={22} />
        <IconAmericanExpressLogo1 width={35} height={22} />
        <IconPaypalLogo width={35} height={22} />
        <IconLogoPaiement width={35} height={22} />
      </CardsRow>
    </LogosSlide>
  );
}

type Props = {
  /** Ajoute une slide avec les logos des moyens de paiement (CB, Visa, Alma…). */
  showPaymentLogos?: boolean;
  /** Panier éligible au paiement en plusieurs fois Alma (champ `isAlma`). */
  isAlma?: boolean;
};

export default function BasketBarReassuranceSlider({
  showPaymentLogos = true,
  isAlma = false,
}: Props) {
  const { i18n } = useTranslation('shipping', { useSuspense: false });
  const [index, setIndex] = useState(0);
  const [tick, setTick] = useState(0);

  // La réassurance Alma n'est affichée que sur les marchés francophones et
  // si le panier est éligible (isAlma).
  const isFr = (i18n.language || '').toLowerCase().startsWith('fr');
  const showAlma = isFr && isAlma;

  const items = [
    ServiceClient,
    EasyReturns,
    ...(showAlma ? [AlmaInstallments] : []),
    SecurePayment,
    ...(showPaymentLogos ? [PaymentLogos] : []),
  ];

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % items.length);
      setTick((n) => n + 1);
    }, 4000);
    return () => clearInterval(id);
  }, [items.length]);

  const Item = items[index % items.length];

  return (
    <SliderRow key={tick}>
      <Item />
    </SliderRow>
  );
}
